import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useEffect, useImperativeHandle } from 'react';
import { DialogWrapper } from './dialog-wrapper.js';
import { Calendar } from '../ui/calendar.js';
import { common } from '../../assets/js/common.js';
import { CalendarMonth } from './calendar-month.js';
import { LineIcon } from '../ui/icons/LineIcon.js';
import { useDevice } from '../../contexts/DeviceContext.js';
import { cn } from '../../lib/utils.js';
import { useTranslation } from 'react-i18next';
import { useInputCaret } from '../../hooks/use-input-caret.js';
import { MessageDialog } from './alert.js';

const DatePickerSingle = forwardRef(
  ({
    mode = "input",
    id,
    className,
    value,
    onChange,
    onClick,
    disabled,
    disabledDate,
    variants = "day",
    startYear = new Date(1900, 0, 1),
    endYear = new Date(2100, 0, 1),
    setPickerOpen,
    error = false,
    open = false
  }, ref) => {
    const { isDesktop } = useDevice();
    const [dateVal, setDateVal] = useState();
    const [openFlag, setOpenFlag] = useState(open);
    const [dateText, setDateText] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [valueChange, setValueChange] = useState(false);
    const prevOpenFlagRef = useRef(openFlag);
    const calendarBtnRef = useRef(null);
    const { i18n } = useTranslation();
    useEffect(() => {
      setPickerOpen?.(openFlag);
      if (prevOpenFlagRef.current === false) return;
      prevOpenFlagRef.current = true;
      console.log("데이트피커 캘린더 openFlag:", openFlag, open);
      window.dispatchEvent(
        new CustomEvent("datepicker:visibility-change", {
          detail: {
            open: openFlag
          }
        })
      );
    }, [openFlag]);
    useEffect(() => {
      setValueChange(true);
      const changeDate = value && value !== "" ? common.changeFormat(value) : void 0;
      setDateVal(changeDate);
      changeDateVal(changeDate);
    }, [value]);
    const changeDateVal = (val, confirm) => {
      const currentDate = val;
      if (!currentDate) {
        setDateText("");
        if (!valueChange) onChange?.("");
        return;
      }
      const next = variants === "day" ? common.setFormatDate(currentDate) ?? "" : common.setFormatMonth(currentDate) ?? "";
      const finalDate = onChange?.(next);
      if (finalDate) {
        setDateText(finalDate);
        setDateVal(common.changeFormat(finalDate));
      } else {
        setDateText(next);
      }
      if (confirm === true) onClick?.();
      setValueChange(false);
    };
    const checkDateInput = ({ val, type }) => {
      if (mode === "picker") {
        if (!isDesktop) {
          setOpenFlag?.(true);
        }
      } else {
        setOpenFlag?.(false);
      }
      if (!val) {
        setDateVal(void 0);
        changeDateVal(void 0);
        return;
      }
      if (common.dateCheck({
        dateVal: val.replace(/\./g, ""),
        monthFlag: variants === "month",
        startYear: String(startYear?.getFullYear()),
        endYear: String(endYear?.getFullYear())
      }) === "N") {
        if (type === "blur") {
          setAlertOpen(true);
          setDateVal(void 0);
          changeDateVal(void 0);
        } else {
          setDateText(common.formatYYYYMMDD(val));
        }
        return;
      } else {
        const newVal = common.formatYYYYMMDD(val.replaceAll(".", ""));
        const newDate = common.changeFormat(newVal);
        setDateVal(newDate);
        changeDateVal(newDate);
      }
    };
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current, []);
    useEffect(() => {
      if (value !== void 0 && inputRef.current) {
        inputRef.current.value = value;
      }
    }, [value]);
    const setAllValue = (value2) => {
      checkDateInput({ val: value2, type: "default" });
    };
    const { handleChange, handleKeyDown } = useInputCaret({
      inputRef,
      value: dateText,
      onValueChange: setAllValue
    });
    const rootRef = useRef(null);
    const [title, setTitle] = useState("");
    useEffect(() => {
      const root = rootRef.current;
      if (!root) return;
      const Field = root.closest('[data-slot="field"]');
      const labelField = Field?.querySelector('[data-slot="field-label"]');
      const labelText = labelField?.querySelector(".label-text");
      if (!labelText) return;
      const clone = labelText.cloneNode(true);
      clone.querySelectorAll('[role="group"]').forEach((el) => el.remove());
      const text = clone.textContent?.trim() ?? "";
      console.log(clone);
      setTitle(text);
    }, []);
    const openCalendar = () => {
      calendarBtnRef.current?.focus();
      setOpenFlag(true);
    };
    const closeCalendar = () => {
      setOpenFlag(false);
      setTimeout(() => calendarBtnRef.current?.focus(), 0);
    };
    const returnFocus = (e) => {
      e.preventDefault();
      calendarBtnRef.current?.focus();
    };
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: cn(
            "flex items-center gap-4 rounded-[12px] border border-basic-04 w-full h-14 pl-4 pr-3 bg-base-white",
            "focus-within:border-interactive-primary-default-01 focus-within:shadow-[inset_0_0_0_1px_var(--color-border-interactive-primary-default-01)]",
            isDesktop && "h-[66px] pl-5 pr-4",
            disabled && "bg-state-disabled-02 border-state-disabled-02 pointer-events-none",
            error ? "!border-interactive-danger !shadow-[inset_0_0_0_1px_var(--color-border-interactive-danger)]" : "",
            className
          ),
          onClick: mode === "picker" && !isDesktop ? () => setOpenFlag(true) : void 0,
          ref: rootRef,
          children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-1", children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                inputMode: "numeric",
                id,
                ref: inputRef,
                type: "text",
                className: cn(
                  "bg-base-white border-none body-lg text-basic-02 !w-[calc(100%-34px)]",
                  !isDesktop && "outline-none",
                  disabled && "text-state-disabled-04",
                  mode === "picker" && isDesktop && "focus-visible:outline-none",
                  mode === "picker" && "bg-base-white",
                  mode === "picker" && "read-only:bg-base-white disabled:!bg-state-disabled-02"
                ),
                title: disabled || mode === "picker" ? `${title}` : `${title} 입력`,
                value: dateText,
                maxLength: variants === "month" ? 7 : 10,
                onChange: handleChange,
                onKeyDown: handleKeyDown,
                onBlur: (e) => {
                  checkDateInput({ val: e.target.value, type: "blur" });
                },
                disabled,
                readOnly: mode === "picker",
                placeholder: variants === "day" ? "YYYY.MM.DD" : "YYYY.MM"
              }
            ),
            /* @__PURE__ */ jsx(
              "button",
              {
                ref: calendarBtnRef,
                onClick: openCalendar,
                disabled,
                "aria-label": `${title} 선택`,
                children: /* @__PURE__ */ jsx(LineIcon, { icoColor: "bg-icon-basic-01", icoName: "ico-calendar" })
              }
            )
          ] })
        }
      ),
      /* @__PURE__ */ jsxs(
        DialogWrapper,
        {
          title: i18n.language === "ko" ? "날짜 선택" : "Date picker",
          open: openFlag,
          setOpen: (next) => next ? setOpenFlag(true) : closeCalendar(),
          footer: [
            {
              type: "primary",
              title: i18n.language === "ko" ? "확인" : "Confirm",
              hasClose: true,
              handler: () => {
                changeDateVal(dateVal, true);
              }
            }
          ],
          onCloseAutoFocus: returnFocus,
          children: [
            variants === "day" && /* @__PURE__ */ jsx(
              Calendar,
              {
                mode: "single",
                selected: dateVal,
                onSelect: setDateVal,
                defaultMonth: dateVal,
                className: "w-full",
                captionLayout: "dropdown",
                startMonth: startYear,
                endMonth: endYear,
                disabled: disabledDate,
                labels: {
                  labelPrevious: () => "이전 달로 이동",
                  labelNext: () => "다음 달로 이동"
                },
                required: true
              }
            ),
            variants === "month" && /* @__PURE__ */ jsx(
              CalendarMonth,
              {
                startYear: startYear?.getFullYear(),
                endYear: endYear?.getFullYear(),
                value: dateVal,
                onChange: setDateVal,
                disabled: disabledDate
              }
            )
          ]
        }
      ),
      alertOpen && /* @__PURE__ */ jsx(
        MessageDialog,
        {
          type: "alert",
          open: alertOpen,
          openChange: setAlertOpen,
          onConfirm: () => setAlertOpen(false),
          okBtnLabel: i18n.language === "ko" ? "확인" : "Confirm",
          children: i18n.language === "ko" ? /* @__PURE__ */ jsx("div", { children: "날짜형식에 맞춰 입력해 주세요." }) : /* @__PURE__ */ jsx("div", { children: "Enter date in valid format." })
        }
      )
    ] });
  }
);

export { DatePickerSingle };
